import React, { useState, useRef, useEffect } from 'react';
import INDIAN_STATES from '../../constants/indianStates';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-hot-toast';
import {
  ArrowLeft,
  MapPin,
  Plus,
  Trash2,
  Save,
  Building2
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../utils/api';

// Import Leaflet
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix Leaflet marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
});

// Helper function to round coordinates to 6 decimal places
const roundCoordinate = (coord) => {
  return parseFloat(parseFloat(coord).toFixed(6));
};

const SERVICE_MODES = [
  {
    id: 'normal_only',
    label: 'Normal Ride only',
    icon: '🚕',
    services: ['RIDE'],
    desc: 'Drivers here are offered ordinary rides only.',
  },
  {
    id: 'acting_only',
    label: 'Acting Driver only',
    icon: '🚘',
    services: ['ACTING'],
    desc: "Drivers here are offered acting jobs only — they drive the customer's own car.",
  },
  {
    id: 'both',
    label: 'Both',
    icon: '🔄',
    services: ['RIDE', 'ACTING'],
    desc: 'Drivers here can be offered either kind of job.',
  },
];

/*
    Which mode an existing enabledServices array represents.

    Anything without ACTING reads as normal-only, including a missing or empty
    array — an office saved before this field existed sells ordinary rides, and
    must keep doing so until an admin says otherwise.
*/
const modeOf = (services) => {
  const has = (svc) => Array.isArray(services) && services.includes(svc);
  if (has('ACTING') && has('RIDE')) return 'both';
  if (has('ACTING')) return 'acting_only';
  return 'normal_only';
};

const EditRegionalOffice = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const queryClient = useQueryClient();
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null); // Use ref to store map instance
  const [mapInitialized, setMapInitialized] = useState(false); // Track map initialization
  const [markers, setMarkers] = useState([]);
  const [polygons, setPolygons] = useState([]);
  const [currentPolygon, setCurrentPolygon] = useState([]);
  const [isDrawingPolygon, setIsDrawingPolygon] = useState(false);

  const { data, isLoading, error } = useQuery(
    ['regional-office', id],
    () => api.get(`/regional-offices/${id}`),
    {
      select: (data) => data?.data?.data || data?.data
    }
  );

  const [formData, setFormData] = useState({
    name: '',
    address: '',
    state: '',
    location: null,
    contactInfo: {
      phone: '',
      email: '',
      website: ''
    },
    servingAreas: [],
    isActive: true,
    fareConfig: null,
    actingDriverFareConfig: null,
    enabledServices: ['RIDE'],
    // Offices tried after this one's own drivers, in order: [{ officeId, windowSeconds }].
    nearbyOffices: []
  });

  // Every office, for the "Nearby offices" picker.
  const { data: allOffices } = useQuery(
    ['regional-offices', 'picker'],
    () => api.get('/regional-offices', { params: { limit: 500, page: 1 } }),
    {
      select: (res) => (res?.data?.data || res?.data)?.offices || [],
      enabled: hasPermission('regional_offices', 'write')
    }
  );

  // Fetch fare configs for dropdown
  const { data: fareConfigs } = useQuery(
    ['fare-configs'],
    () => api.get('/regional-offices/fare-configs'),
    {
      select: (data) => data?.data?.data || data?.data,
      enabled: hasPermission('regional_offices', 'write') // Only fetch if user has write permission
    }
  );

  // Fetch acting-driver fare configs for dropdown
  const { data: actingFareConfigs } = useQuery(
    ['acting-fare-configs'],
    () => api.get('/regional-offices/acting-fare-configs'),
    {
      select: (data) => data?.data?.data || data?.data,
      enabled: hasPermission('regional_offices', 'write')
    }
  );

  // Update form data when office data is loaded
  useEffect(() => {
    if (data) {
      // Handle location in GeoJSON format from API
      let locationData = null;
      if (data.location && data.location.coordinates) {
        // Convert GeoJSON format to simple array format
        locationData = data.location.coordinates; // [longitude, latitude]
      }

      // The API returns serving areas GeoJSON-wrapped ({ coordinates: [ring],
      // properties: { name } }), but the drawer and handleSubmit work with the
      // flat shape ({ coordinates: ring, name }). Normalize loaded areas to the
      // flat shape so an un-redrawn area doesn't crash submit with "Invalid
      // coordinate" (roundCoordinate would receive a ring instead of a number).
      // The API can nest the ring more than one level deep (it returns
      // `coordinates: [polygon.coordinates]`, and polygon.coordinates is itself
      // `[ring]`, giving `[[ring]]`). Descend until we reach the actual ring —
      // an array of [lng, lat] number pairs — so submit maps over points, not
      // rings (which previously collapsed the polygon to one bogus pair).
      const extractRing = (coords) => {
        let node = coords;
        while (Array.isArray(node) && Array.isArray(node[0]) && typeof node[0][0] !== 'number') {
          node = node[0];
        }
        return Array.isArray(node) ? node : [];
      };
      const normalizeLoadedArea = (area) => ({
        coordinates: extractRing(area.coordinates),
        name: area.properties?.name || area.name || '',
        description: area.properties?.description || area.description || ''
      });
      const normalizedServingAreas = (data.servingAreas || []).map(normalizeLoadedArea);

      setFormData({
        name: data.name || '',
        address: data.address || '',
        state: data.state || '',
        location: locationData,
        contactInfo: {
          phone: data.contactInfo?.phone || '',
          email: data.contactInfo?.email || '',
          website: data.contactInfo?.website || ''
        },
        servingAreas: normalizedServingAreas,
        isActive: data.isActive !== undefined ? data.isActive : true,
        fareConfig: data.fareConfig?._id || null,
        actingDriverFareConfig: data.actingDriverFareConfig?._id || data.actingDriverFareConfig || null,
        enabledServices: Array.isArray(data.enabledServices) && data.enabledServices.length
          ? data.enabledServices
          : ['RIDE'],
        nearbyOffices: (Array.isArray(data.nearbyOffices) ? data.nearbyOffices : []).map((n) => ({
          officeId: String(n?.officeId?._id || n?.officeId || ''),
          windowSeconds: Number(n?.windowSeconds) || 0
        })).filter((n) => n.officeId)
      });
      setPolygons(normalizedServingAreas);
    }
  }, [data]);

  const updateMutation = useMutation(
    (data) => api.put(`/regional-offices/${id}`, data),
    {
      onSuccess: () => {
        toast.success('Regional office updated successfully');
        queryClient.invalidateQueries(['regional-offices']);
        navigate('/regional-offices');
      },
      onError: (error) => {
        toast.error(error?.response?.data?.message || 'Failed to update regional office');
      }
    }
  );

  /*
      What this office sells. Mirrors MODE_SERVICES in AppConfig's
      CreateAppConfig.js, so both screens describe a region the same way.

      This replaced an Acting on/off toggle layered over a hardcoded ['RIDE']
      baseline, which could only ever produce ['RIDE'] or ['RIDE','ACTING'] —
      there was no way to say "this office does acting ONLY". That was not
      cosmetic: ActingDriverProfile.optIn grants RIDE whenever this array
      contains it, so every acting driver in an acting-only city was also made
      a ride driver, with no vehicle of their own to bring.
  */
  // ---- Nearby offices: the order here is the order a booking is offered to them ----
  const MAX_NEARBY_OFFICES = 4;
  const nearbyChoices = (allOffices || []).filter(
    (o) => String(o._id) !== String(id) && !formData.nearbyOffices.some((n) => n.officeId === String(o._id))
  );
  const officeName = (officeId) => (allOffices || []).find((o) => String(o._id) === String(officeId))?.name || officeId;
  const addNearbyOffice = (officeId) => {
    if (!officeId || formData.nearbyOffices.length >= MAX_NEARBY_OFFICES) return;
    setFormData(prev => ({ ...prev, nearbyOffices: [...prev.nearbyOffices, { officeId, windowSeconds: 0 }] }));
  };
  const removeNearbyOffice = (index) =>
    setFormData(prev => ({ ...prev, nearbyOffices: prev.nearbyOffices.filter((_, i) => i !== index) }));
  const moveNearbyOffice = (index, delta) =>
    setFormData(prev => {
      const next = [...prev.nearbyOffices];
      const to = index + delta;
      if (to < 0 || to >= next.length) return prev;
      [next[index], next[to]] = [next[to], next[index]];
      return { ...prev, nearbyOffices: next };
    });
  const setNearbyWindow = (index, value) =>
    setFormData(prev => ({
      ...prev,
      nearbyOffices: prev.nearbyOffices.map((n, i) =>
        i === index ? { ...n, windowSeconds: value === '' ? 0 : Math.max(0, Math.min(600, parseInt(value, 10) || 0)) } : n)
    }));

  const setServiceMode = (modeId) => {
    const mode = SERVICE_MODES.find(m => m.id === modeId);
    if (!mode) return;
    setFormData(prev => ({ ...prev, enabledServices: [...mode.services] }));
  };

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleLocationSelect = (lat, lng) => {
    const roundedLat = roundCoordinate(lat);
    const roundedLng = roundCoordinate(lng);
    
    setFormData(prev => ({
      ...prev,
      location: [roundedLng, roundedLat] // GeoJSON format: [longitude, latitude]
    }));

    // Add marker to map
    if (mapInstanceRef.current) {
      // Clear existing markers
      markers.forEach(marker => mapInstanceRef.current.removeLayer(marker));
      
      const newMarker = L.marker([roundedLat, roundedLng]).addTo(mapInstanceRef.current);
      setMarkers([newMarker]);
    }
  };

  const startDrawingPolygon = () => {
    if (!mapInstanceRef.current) {
      console.warn('Map not ready for polygon drawing');
      toast.error('Map is not ready. Please wait a moment and try again.');
      return;
    }
    
    console.log('Starting polygon drawing, current isDrawingPolygon:', isDrawingPolygon); // Debug log
    setIsDrawingPolygon(true);
    setCurrentPolygon([]);
    toast.success('Click on the map to add polygon points');
  };

  // Helper function to check if map is ready
  const isMapReady = () => {
    return mapInstanceRef.current && 
           mapInstanceRef.current._container && 
           typeof mapInstanceRef.current.addLayer === 'function';
  };

  const addPointToPolygon = (lat, lng) => {
    console.log('addPointToPolygon called, isDrawingPolygon:', isDrawingPolygon, 'isMapReady:', isMapReady()); // Debug log
    if (isDrawingPolygon && isMapReady()) {
      try {
        const roundedLat = roundCoordinate(lat);
        const roundedLng = roundCoordinate(lng);
        
        setCurrentPolygon(prev => [...prev, [roundedLng, roundedLat]]);
        
        // Add marker to map
        const marker = L.marker([roundedLat, roundedLng]).addTo(mapInstanceRef.current);
        setMarkers(prev => [...prev, marker]);
        console.log('Point added to polygon successfully'); // Debug log
      } catch (error) {
        console.error('Error adding point to polygon:', error);
        toast.error('Error adding point. Please try again.');
      }
    } else {
      console.warn('Cannot add point: map not ready or not in drawing mode', {
        isDrawingPolygon,
        hasMap: !!mapInstanceRef.current,
        isMapReady: isMapReady()
      });
    }
  };

  const finishPolygon = () => {
    if (currentPolygon.length >= 3) {
      try {
        const newPolygon = {
          coordinates: [...currentPolygon, currentPolygon[0]], // Close the polygon
          name: `Area ${polygons.length + 1}`,
          description: ''
        };

        setPolygons(prev => [...prev, newPolygon]);
        setFormData(prev => ({
          ...prev,
          servingAreas: [...prev.servingAreas, newPolygon]
        }));

        // Draw polygon on map
        if (isMapReady()) {
          const latLngs = currentPolygon.map(([lng, lat]) => [lat, lng]);
          const polygon = L.polygon(latLngs, { color: 'red' }).addTo(mapInstanceRef.current);
          setMarkers(prev => [...prev, polygon]);
        }
      } catch (error) {
        console.error('Error finishing polygon:', error);
        toast.error('Error creating polygon. Please try again.');
      }
    } else {
      toast.error('Polygon must have at least 3 points');
    }

    setIsDrawingPolygon(false);
    setCurrentPolygon([]);
  };

  const removePolygon = (index) => {
    setPolygons(prev => prev.filter((_, i) => i !== index));
    setFormData(prev => ({
      ...prev,
      servingAreas: prev.servingAreas.filter((_, i) => i !== index)
    }));

    // Remove from map
    if (mapInstanceRef.current && markers[index + 1]) { // +1 because first marker is location
      mapInstanceRef.current.removeLayer(markers[index + 1]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.address || !formData.location) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Ensure all coordinates are properly rounded before submission
    const submissionData = {
      ...formData,
      location: formData.location ? {
        type: 'Point',
        coordinates: [
          roundCoordinate(formData.location[0]),
          roundCoordinate(formData.location[1])
        ]
      } : null,
      servingAreas: formData.servingAreas.map(area => ({
        ...area,
        coordinates: area.coordinates.map(coord => [
          roundCoordinate(coord[0]),
          roundCoordinate(coord[1])
        ])
      }))
    };

    updateMutation.mutate(submissionData);
  };

  // Initialize map when component mounts and data is loaded
  useEffect(() => {
    if (!mapRef.current || !data) return;

    // Add a small delay to ensure the container is fully rendered
    const timer = setTimeout(() => {
      try {
        // Check if map is already initialized
        if (mapInstanceRef.current) {
          mapInstanceRef.current.remove();
        }

        // Ensure the container is ready
        if (!mapRef.current || !mapRef.current.appendChild) {
          console.error('Map container not ready');
          return;
        }

        // Initialize Leaflet map
        let mapCenter = [20.5937, 78.9629]; // Default center of India
        let mapZoom = 5;

        // If we have existing location, center on it
        if (data.location && data.location.coordinates) {
          const [lng, lat] = data.location.coordinates;
          mapCenter = [lat, lng];
          mapZoom = 10; // Closer zoom for specific location
        }

        const leafletMap = L.map(mapRef.current, {
          center: mapCenter,
          zoom: mapZoom,
          zoomControl: true,
          attributionControl: true
        });

        // Add OpenStreetMap tiles - simple configuration
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '© OpenStreetMap contributors',
          maxZoom: 19,
          subdomains: ['a', 'b', 'c']
        }).addTo(leafletMap);

        // Ensure map is properly initialized
        if (!leafletMap._container) {
          console.error('Map container not created properly');
          return;
        }

        mapInstanceRef.current = leafletMap;
        setMapInitialized(true); // Set map initialized to true

        // Add existing location marker if available
        if (data.location && data.location.coordinates) {
          const [lng, lat] = data.location.coordinates;
          const marker = L.marker([lat, lng]).addTo(leafletMap);
          setMarkers(prev => [marker]);
        }

        // Add existing serving areas
        if (data.servingAreas && data.servingAreas.length > 0) {
          data.servingAreas.forEach((area) => {
            if (area.coordinates && area.coordinates[0]) {
              const latLngs = area.coordinates[0].map(([lng, lat]) => [lat, lng]);
              const polygon = L.polygon(latLngs, { color: 'red' }).addTo(leafletMap);
              setMarkers(prev => [...prev, polygon]);
            }
          });
        }

        console.log('Map initialized successfully');
      } catch (error) {
        console.error('Error initializing map:', error);
      }
    }, 200);

    return () => {
      clearTimeout(timer);
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
      }
    };
  }, [data]); // Only depend on data

  // Update map click handler when drawing state changes
  useEffect(() => {
    if (!mapInstanceRef.current || !mapInitialized) return;

    // Remove existing click handler
    mapInstanceRef.current.off('click');

    // Add new click handler based on current state
    mapInstanceRef.current.on('click', (e) => {
      console.log('Map clicked, isDrawingPolygon:', isDrawingPolygon); // Debug log
      if (!isDrawingPolygon) {
        handleLocationSelect(e.latlng.lat, e.latlng.lng);
      } else {
        addPointToPolygon(e.latlng.lat, e.latlng.lng);
      }
    });
  }, [mapInitialized, isDrawingPolygon]); // Depend on map initialization and drawing state

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="empty-state">
        <Building2 className="empty-state-icon" />
        <h3 className="empty-state-title">Failed to load regional office</h3>
        <p className="empty-state-description">
          {error?.response?.data?.message || 'An error occurred while loading regional office'}
        </p>
      </div>
    );
  }

  if (!hasPermission('regional_offices', 'write')) {
    return (
      <div className="empty-state">
        <Building2 className="empty-state-icon" />
        <h3 className="empty-state-title">Access Denied</h3>
        <p className="empty-state-description">
          You don't have permission to edit regional offices.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="page-header">
        <div className="px-4 py-5 sm:px-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/regional-offices')}
                className="text-gray-400 hover:text-gray-600"
              >
                <ArrowLeft className="h-6 w-6" />
              </button>
              <div>
                <h1 className="page-title">Edit Regional Office</h1>
                <p className="page-subtitle">Update regional office information</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Form Fields */}
          <div className="space-y-6">
            {/* Basic Information */}
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">Basic Information</h3>
              </div>
              <div className="card-body space-y-4">
                <div>
                  <label className="form-label">Office Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    className="input"
                    placeholder="Enter office name"
                    required
                  />
                </div>

                <div>
                  <label className="form-label">Address *</label>
                  <textarea
                    value={formData.address}
                    onChange={(e) => handleInputChange('address', e.target.value)}
                    className="input"
                    rows={3}
                    placeholder="Enter office address"
                    required
                  />
                </div>

                <div>
                  <label className="form-label">State</label>
                  <select
                    value={formData.state || ''}
                    onChange={(e) => handleInputChange('state', e.target.value)}
                    className="input"
                  >
                    <option value="">Select state…</option>
                    {INDIAN_STATES.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-400 mt-1">Drives state-wise app config for trips in this office’s area.</p>
                </div>

                <div>
                  <label className="form-label">Location *</label>
                  <div className="text-sm text-gray-500 mb-2">
                    Click on the map to update the office location
                  </div>
                  {formData.location && (
                    <div className="flex items-center space-x-2 text-sm text-gray-600">
                      <MapPin className="h-4 w-4" />
                      <span>
                        {formData.location && formData.location[1] ? formData.location[1].toFixed(6) : 'N/A'}, {formData.location && formData.location[0] ? formData.location[0].toFixed(6) : 'N/A'}
                      </span>
                    </div>
                  )}
                </div>

                <div>
                  <label className="form-label">Status</label>
                  <select
                    value={formData.isActive}
                    onChange={(e) => handleInputChange('isActive', e.target.value === 'true')}
                    className="input"
                  >
                    <option value={true}>Active</option>
                    <option value={false}>Inactive</option>
                  </select>
                </div>

                {/* Fare Config Dropdown - Only for Super Admin */}
                {hasPermission('regional_offices', 'write') && (
                  <div>
                    <label className="form-label">Fare Configuration</label>
                    <select
                      value={formData.fareConfig || ''}
                      onChange={(e) => handleInputChange('fareConfig', e.target.value || null)}
                      className="input"
                    >
                      <option value="">Select Fare Configuration</option>
                      {fareConfigs?.map((config) => (
                        <option key={config._id} value={config._id}>
                          {config.regionCode}
                        </option>
                      ))}
                    </select>
                    <div className="text-sm text-gray-500 mt-1">
                      Select the fare configuration for this regional office
                    </div>
                  </div>
                )}

                {/* Acting Driver Fare Dropdown - Only for Super Admin */}
                {hasPermission('regional_offices', 'write') && (
                  <div>
                    <label className="form-label">Acting Driver Fare</label>
                    <select
                      value={formData.actingDriverFareConfig || ''}
                      onChange={(e) => handleInputChange('actingDriverFareConfig', e.target.value || null)}
                      className="input"
                    >
                      <option value="">Select Acting Driver Fare</option>
                      {actingFareConfigs?.map((config) => (
                        <option key={config._id} value={config._id}>
                          {config.regionCode}{config.isActive === false ? ' (inactive)' : ''}
                        </option>
                      ))}
                    </select>
                    <div className="text-sm text-gray-500 mt-1">
                      Acting-driver (time-based) fare for this office. Needed for acting-driver-only regions.
                    </div>
                  </div>
                )}

                {/* What this office sells. This array is what
                    ActingDriverController.resolveEnabledServices reads, and what
                    ActingDriverProfile.optIn grants a driver on opt-in — so
                    'Acting Driver only' is the setting that stops an acting
                    driver also being made a ride driver. Separate from the fare
                    dropdown above: a fare config with the service off means
                    acting is priced but not yet turned on for this office. */}
                {hasPermission('regional_offices', 'write') && (
                  <div>
                    <label className="form-label">Services Offered</label>
                    <div className="space-y-2">
                      {SERVICE_MODES.map((mode) => {
                        const selected = modeOf(formData.enabledServices) === mode.id;
                        return (
                          <div
                            key={mode.id}
                            onClick={() => setServiceMode(mode.id)}
                            className={`flex items-center justify-between p-4 rounded-lg border-2 cursor-pointer select-none transition-all
                              ${selected ? 'border-purple-400 bg-purple-50' : 'border-gray-200 bg-gray-50'}`}
                          >
                            <div className="flex items-center gap-3">
                              <span className="text-2xl">{mode.icon}</span>
                              <div>
                                <p className="text-sm font-semibold text-gray-800">{mode.label}</p>
                                <p className="text-xs text-gray-500">{mode.desc}</p>
                              </div>
                            </div>
                            <div className={`w-5 h-5 rounded-full border-2 flex-shrink-0 flex items-center justify-center transition-colors ${selected ? 'border-purple-500' : 'border-gray-300'}`}>
                              {selected && <div className="w-2.5 h-2.5 rounded-full bg-purple-500" />}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Nearby offices: where a booking goes after THIS office's own drivers. */}
            {hasPermission('regional_offices', 'write') && (
              <div className="card">
                <div className="card-header">
                  <h3 className="card-title">Nearby Offices</h3>
                </div>
                <div className="card-body space-y-3">
                  <div className="text-sm text-gray-500">
                    A booking in this office is offered to this office's own drivers first. If none
                    accept, it goes to these offices, one after another, in the order below — and only
                    then to every driver. Needs "Offer to the booking's own office first" switched on
                    in Ride Match Settings. Up to {MAX_NEARBY_OFFICES}.
                  </div>

                  {formData.nearbyOffices.length === 0 && (
                    <div className="text-sm text-gray-400">None — after its own drivers, a booking goes straight to everyone.</div>
                  )}

                  {formData.nearbyOffices.map((n, index) => (
                    <div key={n.officeId} className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 bg-gray-50">
                      <span className="w-6 h-6 rounded-full bg-purple-100 text-purple-700 text-xs font-semibold flex items-center justify-center flex-shrink-0">
                        {index + 1}
                      </span>
                      <span className="flex-1 text-sm font-medium text-gray-800">{officeName(n.officeId)}</span>
                      <label className="text-xs text-gray-500 flex items-center gap-1">
                        Wait
                        <input
                          type="number"
                          min="0"
                          max="600"
                          value={n.windowSeconds || ''}
                          placeholder="default"
                          onChange={(e) => setNearbyWindow(index, e.target.value)}
                          className="input w-20 py-1"
                        />
                        s
                      </label>
                      <button type="button" className="btn btn-secondary btn-sm" disabled={index === 0} onClick={() => moveNearbyOffice(index, -1)} title="Try earlier">↑</button>
                      <button type="button" className="btn btn-secondary btn-sm" disabled={index === formData.nearbyOffices.length - 1} onClick={() => moveNearbyOffice(index, 1)} title="Try later">↓</button>
                      <button type="button" className="btn btn-secondary btn-sm" onClick={() => removeNearbyOffice(index)} title="Remove">✕</button>
                    </div>
                  ))}

                  {formData.nearbyOffices.length < MAX_NEARBY_OFFICES && (
                    <select
                      className="input"
                      value=""
                      onChange={(e) => addNearbyOffice(e.target.value)}
                    >
                      <option value="">Add a nearby office…</option>
                      {nearbyChoices.map((o) => (
                        <option key={o._id} value={o._id}>
                          {o.name}{o.state ? ` (${o.state})` : ''}{o.isActive === false ? ' — inactive' : ''}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              </div>
            )}

            {/* Contact Information */}
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">Contact Information</h3>
              </div>
              <div className="card-body space-y-4">
                <div>
                  <label className="form-label">Phone</label>
                  <input
                    type="tel"
                    value={formData.contactInfo.phone}
                    onChange={(e) => handleInputChange('contactInfo.phone', e.target.value)}
                    className="input"
                    placeholder="Enter phone number"
                  />
                </div>

                <div>
                  <label className="form-label">Email</label>
                  <input
                    type="email"
                    value={formData.contactInfo.email}
                    onChange={(e) => handleInputChange('contactInfo.email', e.target.value)}
                    className="input"
                    placeholder="Enter email address"
                  />
                </div>

                <div>
                  <label className="form-label">Website</label>
                  <input
                    type="url"
                    value={formData.contactInfo.website}
                    onChange={(e) => handleInputChange('contactInfo.website', e.target.value)}
                    className="input"
                    placeholder="Enter website URL"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Map and Serving Areas */}
          <div className="space-y-6">
            {/* Map */}
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">Location & Serving Areas</h3>
              </div>
              <div className="card-body">
                <div className="mb-4 space-y-2">
                  <div className="text-sm text-gray-600">
                    <strong>Instructions:</strong>
                  </div>
                  <div className="text-sm text-gray-500">
                    1. Click on the map to update office location
                  </div>
                  <div className="text-sm text-gray-500">
                    2. Click "Start Drawing" to add new serving areas
                  </div>
                  <div className="text-sm text-gray-500">
                    3. Click on map to add polygon points
                  </div>
                  <div className="text-sm text-gray-500">
                    4. Click "Finish Polygon" to complete the area
                  </div>
                </div>

                <div className="flex space-x-2 mb-4">
                  <button
                    type="button"
                    onClick={startDrawingPolygon}
                    disabled={isDrawingPolygon}
                    className="btn btn-sm btn-secondary"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Start Drawing
                  </button>
                  
                  {isDrawingPolygon && (
                    <button
                      type="button"
                      onClick={finishPolygon}
                      className="btn btn-sm btn-success"
                    >
                      Finish Polygon
                    </button>
                  )}
                </div>

                <div
                  ref={mapRef}
                  className="w-full h-96 rounded-lg border border-gray-300"
                ></div>
              </div>
            </div>

            {/* Serving Areas List */}
            {polygons.length > 0 && (
              <div className="card">
                <div className="card-header">
                  <h3 className="card-title">Serving Areas</h3>
                </div>
                <div className="card-body">
                  <div className="space-y-3">
                    {polygons.map((polygon, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <div className="font-medium text-gray-900">{polygon.name}</div>
                          <div className="text-sm text-gray-500">
                            {polygon.coordinates.length} points
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => removePolygon(index)}
                          className="btn btn-sm btn-danger"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate('/regional-offices')}
            className="btn btn-secondary"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={updateMutation.isLoading}
            className="btn btn-primary"
          >
            <Save className="h-4 w-4 mr-2" />
            {updateMutation.isLoading ? 'Updating...' : 'Update Office'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditRegionalOffice; 